import type { DetectionBox, MaskMode, MaskSettings, Rect } from "@aimosaic/core";
import { sanitizeDownloadName } from "@aimosaic/core";

export async function exportMaskedImage(
  image: HTMLImageElement,
  boxes: DetectionBox[],
  settings: MaskSettings,
  originalName: string,
  outputType: "png" | "jpeg",
  sourceDataUrl?: string
): Promise<{ blob: Blob; filename: string }> {
  const canvas = document.createElement("canvas");
  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas is not available");

  const exportSource = await loadExportImageSource(image, sourceDataUrl);
  context.drawImage(exportSource.source, 0, 0, canvas.width, canvas.height);
  exportSource.close?.();
  for (const box of boxes.filter((candidate) => candidate.action === "mask")) {
    for (const segment of resolveMaskSegmentsForBox(box, settings)) {
      applyMask(context, canvas, segment, resolveMode(settings), settings);
    }
  }

  const mime = outputType === "png" ? "image/png" : "image/jpeg";
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((value) => (value ? resolve(value) : reject(new Error("Export failed"))), mime, 0.92);
  });

  return {
    blob,
    filename: sanitizeDownloadName(originalName, outputType)
  };
}

type BitmapFactory = (
  source: ImageBitmapSource,
  options?: ImageBitmapOptions
) => Promise<ImageBitmap>;

interface ExportImageSource {
  source: CanvasImageSource;
  close?: () => void;
}

interface SampledColor {
  red: number;
  green: number;
  blue: number;
}

export async function loadExportImageSource(
  image: HTMLImageElement,
  sourceDataUrl?: string,
  createBitmap: BitmapFactory | undefined = globalThis.createImageBitmap?.bind(globalThis)
): Promise<ExportImageSource> {
  if (createBitmap && sourceDataUrl) {
    try {
      const bitmap = await createBitmap(dataUrlToBlob(sourceDataUrl), {
        colorSpaceConversion: "none"
      });
      return { source: bitmap, close: () => bitmap.close() };
    } catch {
      return { source: image };
    }
  }

  return { source: image };
}

export function resolveMaskSegmentsForBox(box: DetectionBox, _settings: MaskSettings): Rect[] {
  return [box.bbox];
}

export function resolveBlurMaskPlan(
  rect: Rect,
  canvasSize: { width: number; height: number },
  blurRadius: number
): { source: Rect; target: Rect; overlayAlpha: number } {
  const target = clipRect(roundRect(rect), canvasSize);
  const radius = Number.isFinite(blurRadius) ? Math.max(0, blurRadius) : 0;
  const padding = Math.ceil(Math.max(4, radius * 2));
  const sourceLeft = Math.max(0, target.x - padding);
  const sourceTop = Math.max(0, target.y - padding);
  const sourceRight = Math.min(canvasSize.width, target.x + target.width + padding);
  const sourceBottom = Math.min(canvasSize.height, target.y + target.height + padding);

  return {
    source: {
      x: sourceLeft,
      y: sourceTop,
      width: Math.max(1, sourceRight - sourceLeft),
      height: Math.max(1, sourceBottom - sourceTop)
    },
    target,
    overlayAlpha: resolveBlurPrivacyOverlayAlpha(radius)
  };
}

export function resolveBlurOverlayStyle(
  fallbackColor: string,
  sampledColor: SampledColor | undefined,
  blurRadius: number
): { fillStyle: string; alpha: number } {
  const alpha = resolveBlurPrivacyOverlayAlpha(blurRadius);
  if (!sampledColor) {
    return { fillStyle: fallbackColor, alpha };
  }

  return {
    fillStyle: `rgb(${sampledColor.red}, ${sampledColor.green}, ${sampledColor.blue})`,
    alpha
  };
}

function dataUrlToBlob(dataUrl: string): Blob {
  const [metadata, payload] = dataUrl.split(",");
  if (!metadata || !payload) throw new Error("Invalid data URL");
  const mime = metadata.match(/^data:([^;]+)/)?.[1] ?? "application/octet-stream";
  const binary = atob(payload);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new Blob([bytes], { type: mime });
}

function resolveMode(settings: MaskSettings): MaskMode {
  return settings.mode;
}

function applyMask(
  context: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  rect: Rect,
  mode: MaskMode,
  settings: MaskSettings
): void {
  const x = Math.round(rect.x);
  const y = Math.round(rect.y);
  const width = Math.round(rect.width);
  const height = Math.round(rect.height);
  if (width <= 0 || height <= 0) return;

  if (mode === "opaque") {
    context.fillStyle = settings.fillColor;
    context.fillRect(x, y, width, height);
    return;
  }

  if (mode === "blur") {
    const plan = resolveBlurMaskPlan({ x, y, width, height }, { width: canvas.width, height: canvas.height }, settings.blurRadius);
    if (plan.target.width <= 0 || plan.target.height <= 0) return;

    const tmp = document.createElement("canvas");
    tmp.width = Math.max(1, Math.round(plan.source.width));
    tmp.height = Math.max(1, Math.round(plan.source.height));
    const tmpContext = tmp.getContext("2d");
    if (!tmpContext) {
      context.fillStyle = settings.fillColor;
      context.fillRect(plan.target.x, plan.target.y, plan.target.width, plan.target.height);
      return;
    }

    tmpContext.drawImage(
      canvas,
      plan.source.x,
      plan.source.y,
      plan.source.width,
      plan.source.height,
      0,
      0,
      tmp.width,
      tmp.height
    );
    const overlay = resolveBlurOverlayStyle(
      settings.fillColor,
      sampleAverageColor(tmpContext, tmp.width, tmp.height),
      settings.blurRadius
    );

    context.save();
    context.beginPath();
    context.rect(plan.target.x, plan.target.y, plan.target.width, plan.target.height);
    context.clip();
    context.filter = `blur(${settings.blurRadius}px)`;
    context.drawImage(tmp, plan.source.x, plan.source.y, plan.source.width, plan.source.height);
    context.filter = "none";
    context.globalAlpha = overlay.alpha;
    context.fillStyle = overlay.fillStyle;
    context.fillRect(plan.target.x, plan.target.y, plan.target.width, plan.target.height);
    context.restore();
    return;
  }

  const block = Math.max(4, settings.pixelSize);
  const tmp = document.createElement("canvas");
  tmp.width = Math.max(1, Math.ceil(width / block));
  tmp.height = Math.max(1, Math.ceil(height / block));
  const tmpContext = tmp.getContext("2d");
  if (!tmpContext) return;
  tmpContext.imageSmoothingEnabled = false;
  tmpContext.drawImage(canvas, x, y, width, height, 0, 0, tmp.width, tmp.height);
  context.imageSmoothingEnabled = false;
  context.drawImage(tmp, 0, 0, tmp.width, tmp.height, x, y, width, height);
  context.imageSmoothingEnabled = true;
}

function resolveBlurPrivacyOverlayAlpha(blurRadius: number): number {
  return Math.min(0.56, Math.max(0.14, 0.12 + blurRadius / 70));
}

function sampleAverageColor(context: CanvasRenderingContext2D, width: number, height: number): SampledColor | undefined {
  try {
    const imageData = context.getImageData(0, 0, width, height).data;
    let red = 0;
    let green = 0;
    let blue = 0;
    let count = 0;
    const stride = Math.max(4, Math.floor(imageData.length / 800) * 4);

    for (let index = 0; index < imageData.length; index += stride) {
      const alpha = imageData[index + 3] ?? 0;
      if (alpha === 0) continue;
      red += imageData[index] ?? 0;
      green += imageData[index + 1] ?? 0;
      blue += imageData[index + 2] ?? 0;
      count += 1;
    }

    if (count === 0) return undefined;
    return {
      red: Math.round(red / count),
      green: Math.round(green / count),
      blue: Math.round(blue / count)
    };
  } catch {
    return undefined;
  }
}

function roundRect(rect: Rect): Rect {
  return {
    x: Math.round(rect.x),
    y: Math.round(rect.y),
    width: Math.round(rect.width),
    height: Math.round(rect.height)
  };
}

function clipRect(rect: Rect, canvasSize: { width: number; height: number }): Rect {
  const x = Math.min(Math.max(rect.x, 0), canvasSize.width);
  const y = Math.min(Math.max(rect.y, 0), canvasSize.height);
  const right = Math.min(Math.max(rect.x + rect.width, 0), canvasSize.width);
  const bottom = Math.min(Math.max(rect.y + rect.height, 0), canvasSize.height);
  return {
    x,
    y,
    width: Math.max(0, right - x),
    height: Math.max(0, bottom - y)
  };
}
